import { ApiError } from '../api/client';

/** Same 403/400/fallback idiom `order-api-errors.ts`'s `genericApiErrorMessage` already applies. */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `409 CANNOT_CLOSE_WITH_PENDING_ORDERS` drives its own UI branch (an
 * inline `Alert` linking to the Tablero del día) rather than the generic
 * message every other code gets — `ProductionSheetViewPage` needs to
 * tell this one code apart from the rest of `mapProductionError`.
 */
export function isPendingOrdersError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const code = (error.body as { code?: string } | null)?.code;

  return error.status === 409 && code === 'CANNOT_CLOSE_WITH_PENDING_ORDERS';
}

/** `422 OPERATING_UNIT_REQUIRED` is a prompt to pick a unit, not a failure of the screen. */
export function isUnitRequiredError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const code = (error.body as { code?: string } | null)?.code;

  return error.status === 422 && code === 'OPERATING_UNIT_REQUIRED';
}

/** Codes from `ProductionExceptionFilter` (`apps/api` `production-exception.filter.ts`). */
export function mapProductionError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'CANNOT_CLOSE_WITH_PENDING_ORDERS') {
    return 'Todavía hay pedidos enviados sin confirmar para esta fecha. Cerrá el corte de Pedidos antes de cerrar la planilla.';
  }

  if (error.status === 422 && code === 'OPERATING_UNIT_REQUIRED') {
    return 'Elegí un punto de venta (o "Todos los puntos") para ver la planilla.';
  }

  if (error.status === 500 && code === 'PRODUCTION_CONSOLIDATION_MISMATCH') {
    return 'Se detectó una inconsistencia al consolidar los pedidos. Contactá a soporte.';
  }

  if (error.status === 409 && code === 'PRODUCTION_SHEET_NOT_CLOSED') {
    return 'Todavía no se cerró la planilla de esta fecha.';
  }

  if (error.status === 422 && code === 'PRODUCTION_EXPORT_FORMAT_UNSUPPORTED') {
    return 'Formato de exportación no soportado.';
  }

  if (error.status === 500 && code === 'PRODUCTION_CONTEXT_RESOLUTION_FAILED') {
    return 'No pudimos resolver los datos de catálogo o clientes para esta planilla.';
  }

  return genericApiErrorMessage(error);
}
